import {cn} from '../lib/utils'
import * as SwitchPrimitive from '@radix-ui/react-switch'
import {createContext, type ReactNode, useContext, useMemo, useState} from 'react'

interface SwitchContextValue {
  checked: boolean
}

const SwitchContext = createContext<SwitchContextValue | null>(null)

function useSwitchContext(part: string): SwitchContextValue {
  const context = useContext(SwitchContext)
  if (!context) throw new Error(`<${part}> must be used within <Switch>`)
  return context
}

export interface SwitchProps
  extends Omit<SwitchPrimitive.SwitchProps, 'asChild' | 'checked' | 'defaultChecked' | 'value'> {
  /** A `SwitchThumb`, usually holding one `SwitchLabel` per state. */
  children: ReactNode
  /** Controlled state. Pair it with `onCheckedChange`. */
  checked?: boolean
  /** Initial state when the switch is uncontrolled. */
  defaultChecked?: boolean
}

/**
 * Switch
 *
 * A two-state toggle. Built on `@radix-ui/react-switch`, which renders a `button` with
 * `role="switch"` and handles keyboard operation, `aria-checked` and form integration. Compose it
 * from `SwitchThumb` and, optionally, a `SwitchLabel` per state, so the thumb can print the
 * currently selected option (e.g. a unit such as `cm` / `in`).
 *
 * Give the switch an accessible name with `aria-label` or `aria-labelledby` when the thumb labels
 * are not descriptive on their own.
 *
 * @example
 * <Switch aria-label="Unit" checked={isInches} onCheckedChange={setIsInches}>
 *   <SwitchThumb>
 *     <SwitchLabel state="off">cm</SwitchLabel>
 *     <SwitchLabel state="on">in</SwitchLabel>
 *   </SwitchThumb>
 * </Switch>
 */
export function Switch({
  children,
  checked,
  defaultChecked = false,
  onCheckedChange,
  className,
  ...props
}: SwitchProps) {
  const [uncontrolledChecked, setUncontrolledChecked] = useState(defaultChecked)
  const isChecked = checked ?? uncontrolledChecked

  const context = useMemo<SwitchContextValue>(() => ({checked: isChecked}), [isChecked])

  return (
    <SwitchContext.Provider value={context}>
      <SwitchPrimitive.Root
        {...props}
        checked={isChecked}
        className={cn(
          'relative inline-flex h-32 w-[52px] shrink-0 items-center rounded-full bg-surface-alt-2 p-2 transition-[background-color] not-disabled:cursor-pointer hover:not-disabled:bg-surface-alt-2-hover disabled:opacity-50 data-[state=checked]:bg-button data-[state=checked]:hover:not-disabled:bg-button-hover',
          className,
        )}
        onCheckedChange={(next) => {
          setUncontrolledChecked(next)
          onCheckedChange?.(next)
        }}
      >
        {children}
      </SwitchPrimitive.Root>
    </SwitchContext.Provider>
  )
}

export interface SwitchThumbProps {
  /** `SwitchLabel` parts, or any short content. */
  children?: ReactNode
  className?: string
}

/** The sliding circle. Moves to the end when the switch is on and centers its children. */
export function SwitchThumb({children, className}: SwitchThumbProps) {
  return (
    <SwitchPrimitive.Thumb
      className={cn(
        'pointer-events-none grid size-28 place-items-center rounded-full border border-border bg-surface text-xs leading-xs font-normal whitespace-nowrap text-text transition-[translate] data-[state=checked]:translate-x-20',
        className,
      )}
    >
      {children}
    </SwitchPrimitive.Thumb>
  )
}

export interface SwitchLabelProps {
  /** Which state the label is shown in. */
  state: 'on' | 'off'
  children: ReactNode
}

/** Text shown on the thumb only while the switch is in the given `state`. */
export function SwitchLabel({state, children}: SwitchLabelProps) {
  const {checked} = useSwitchContext('SwitchLabel')

  return (state === 'on') === checked ? <>{children}</> : null
}
